export const isLightColor = (r: number, g: number, b: number): boolean => {
  // Using the relative luminance formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5
}

// RGB to XYZ conversion
export const rgbToXyz = (r: number, g: number, b: number) => {
  // Convert RGB to [0,1] range
  let red = r / 255
  let green = g / 255
  let blue = b / 255

  // Convert to sRGB
  red = red > 0.04045 ? Math.pow((red + 0.055) / 1.055, 2.4) : red / 12.92
  green =
    green > 0.04045 ? Math.pow((green + 0.055) / 1.055, 2.4) : green / 12.92
  blue = blue > 0.04045 ? Math.pow((blue + 0.055) / 1.055, 2.4) : blue / 12.92

  // Convert to XYZ
  const x = (red * 0.4124 + green * 0.3576 + blue * 0.1805) * 100
  const y = (red * 0.2126 + green * 0.7152 + blue * 0.0722) * 100
  const z = (red * 0.0193 + green * 0.1192 + blue * 0.9505) * 100

  return { x, y, z }
}

// XYZ to LAB conversion
export const xyzToLab = (x: number, y: number, z: number) => {
  // D65 illuminant
  const xn = 95.047
  const yn = 100.0
  const zn = 108.883

  const xr = x / xn
  const yr = y / yn
  const zr = z / zn

  const fx = xr > 0.008856 ? Math.pow(xr, 1 / 3) : 7.787 * xr + 16 / 116
  const fy = yr > 0.008856 ? Math.pow(yr, 1 / 3) : 7.787 * yr + 16 / 116
  const fz = zr > 0.008856 ? Math.pow(zr, 1 / 3) : 7.787 * zr + 16 / 116

  const l = 116 * fy - 16
  const a = 500 * (fx - fy)
  const b = 200 * (fy - fz)

  return { l, a, b }
}

// RGB to LAB conversion
export const rgbToLab = (r: number, g: number, b: number) => {
  const { x, y, z } = rgbToXyz(r, g, b)
  return xyzToLab(x, y, z)
}

export const getAverageColor = (
  x: number,
  y: number,
  width: number,
  height: number,
  hiddenCanvasRef: any,
) => {
  if (!hiddenCanvasRef.current)
    return {
      color: '#000000',
      labColor: { L: '00.00', a: '00.00', b: '00.00' },
      isLight: false,
    }

  const ctx = hiddenCanvasRef.current.getContext('2d')
  if (!ctx)
    return {
      color: '#000000',
      labColor: { L: '00.00', a: '00.00', b: '00.00' },
      isLight: false,
    }

  const imageData = ctx.getImageData(x, y, width, height)
  const data = imageData.data
  let r = 0,
    g = 0,
    b = 0

  for (let i = 0; i < data.length; i += 4) {
    r += data[i]
    g += data[i + 1]
    b += data[i + 2]
  }

  const count = data.length / 4
  r = Math.round(r / count)
  g = Math.round(g / count)
  b = Math.round(b / count)

  const { l, a, b: labB } = rgbToLab(r, g, b)

  return {
    color: `rgb(${r}, ${g}, ${b})`,
    labColor: { L: l.toFixed(2), a: a.toFixed(2), b: labB.toFixed(2) },
    isLight: isLightColor(r, g, b),
  }
}
